import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { FilterCatalogItem } from '../types/eventMonitor';

interface EventFilterBarProps {
  selectedTypes: string[];
  selectedSources: string[];
  userId: string;
  since: string;
  availableTypes: FilterCatalogItem[];
  availableSources: FilterCatalogItem[];
  onTypesChange: (types: string[]) => void;
  onSourcesChange: (sources: string[]) => void;
  onUserIdChange: (userId: string) => void;
  onSinceChange: (since: string) => void;
  onApply: () => void;
  onReset: () => void;
  isLoading?: boolean;
}

function FilterCheckItem({
  value,
  count,
  checked,
  onChange,
}: {
  value: string;
  count: number;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-2)',
        padding: 'var(--spacing-1) var(--spacing-2)',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        fontSize: 'var(--font-size-sm)',
        color: 'var(--color-text-primary)',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLLabelElement).style.background = 'var(--color-surface-elevated)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLLabelElement).style.background = 'transparent';
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 14,
          height: 14,
          border: `1px solid ${checked ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
          borderRadius: 3,
          background: checked ? 'var(--color-brand-primary)' : 'var(--color-surface-elevated)',
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          color: 'var(--color-text-inverse)',
        }}
      >
        {checked ? '✓' : ''}
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
        aria-label={value}
      />
      <span
        style={{
          fontFamily: 'var(--font-family-mono)',
          fontSize: 'var(--font-size-xs)',
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </span>
      <span
        style={{
          marginLeft: 'auto',
          fontFamily: 'var(--font-family-mono)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
          flexShrink: 0,
        }}
      >
        {count}
      </span>
    </label>
  );
}

/** EventFilterBar — left-side filter panel */
export function EventFilterBar({
  selectedTypes,
  selectedSources,
  userId,
  since,
  availableTypes,
  availableSources,
  onTypesChange,
  onSourcesChange,
  onUserIdChange,
  onSinceChange,
  onApply,
  onReset,
  isLoading,
}: EventFilterBarProps) {
  const { t } = useTranslation();

  const handleTypeToggle = useCallback(
    (value: string, checked: boolean) => {
      if (checked) {
        onTypesChange([...selectedTypes, value]);
      } else {
        onTypesChange(selectedTypes.filter((t) => t !== value));
      }
    },
    [selectedTypes, onTypesChange]
  );

  const handleSourceToggle = useCallback(
    (value: string, checked: boolean) => {
      if (checked) {
        onSourcesChange([...selectedSources, value]);
      } else {
        onSourcesChange(selectedSources.filter((s) => s !== value));
      }
    },
    [selectedSources, onSourcesChange]
  );

  const inputStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
    outline: 'none',
    width: '100%',
  };

  return (
    <aside
      aria-label={t('eventMonitor.filter.ariaPanel')}
      style={{
        background: 'var(--color-surface-muted)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-4)',
        position: 'sticky',
        top: 'var(--spacing-3)',
        alignSelf: 'start',
      }}
    >
      {/* Panel title */}
      <div
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>{t('eventMonitor.filter.title')}</span>
      </div>

      {/* Event type */}
      {availableTypes.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 'var(--spacing-1)',
            }}
          >
            {t('eventMonitor.filter.type')}
          </span>
          {availableTypes.map((item) => (
            <FilterCheckItem
              key={item.value}
              value={item.value}
              count={item.count}
              checked={selectedTypes.includes(item.value)}
              onChange={(checked) => handleTypeToggle(item.value, checked)}
            />
          ))}
        </div>
      )}

      {/* Source */}
      {availableSources.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 'var(--spacing-1)',
            }}
          >
            {t('eventMonitor.filter.source')}
          </span>
          {availableSources.map((item) => (
            <FilterCheckItem
              key={item.value}
              value={item.value}
              count={item.count}
              checked={selectedSources.includes(item.value)}
              onChange={(checked) => handleSourceToggle(item.value, checked)}
            />
          ))}
        </div>
      )}

      {/* User ID */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
        <label
          htmlFor="em-filter-userId"
          style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            color: 'var(--color-text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {t('eventMonitor.filter.userId')}
        </label>
        <input
          id="em-filter-userId"
          type="text"
          value={userId}
          onChange={(e) => onUserIdChange(e.target.value)}
          placeholder={t('eventMonitor.filter.userIdPlaceholder')}
          style={inputStyle}
          aria-label={t('eventMonitor.filter.ariaUserId')}
        />
      </div>

      {/* Since */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
        <label
          htmlFor="em-filter-since"
          style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            color: 'var(--color-text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {t('eventMonitor.filter.since')}
        </label>
        <input
          id="em-filter-since"
          type="datetime-local"
          value={since}
          onChange={(e) => onSinceChange(e.target.value)}
          style={inputStyle}
          aria-label={t('eventMonitor.filter.ariaSince')}
        />
      </div>

      {/* Buttons */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-2)',
          paddingTop: 'var(--spacing-3)',
          borderTop: '1px dashed var(--color-surface-border)',
        }}
      >
        <button
          type="button"
          onClick={onApply}
          disabled={isLoading}
          aria-label={t('eventMonitor.filter.ariaApply')}
          style={{
            flex: 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--spacing-2)',
            padding: 'var(--spacing-2) var(--spacing-3)',
            background: 'var(--color-brand-primary)',
            color: 'var(--color-text-inverse)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
            fontFamily: 'var(--font-family-sans)',
          }}
        >
          {t('eventMonitor.action.applyFilter')}
        </button>
        <button
          type="button"
          onClick={onReset}
          aria-label={t('eventMonitor.filter.ariaReset')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--spacing-2) var(--spacing-3)',
            background: 'var(--color-surface-elevated)',
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'var(--font-family-sans)',
          }}
        >
          {t('eventMonitor.action.resetFilter')}
        </button>
      </div>
    </aside>
  );
}
